/* Sprint G · Batch 2 — Chat WebSocket client.
 *
 * Encapsula:
 *  - URL construction (http→ws / https→wss + token + organization_id + kind)
 *  - Reconexión automática con backoff exponencial (cap 30s)
 *  - Heartbeat ping cada 25s para mantener viva la conexión a través del proxy
 *  - Callbacks onMessage / onOpen / onClose
 *
 * Uso:
 *   const conn = openChatSocket({
 *     kind: "customer", token, organizationId,
 *     onMessage: (m) => …,
 *     onOpen: () => …,
 *     onClose: () => …,
 *   });
 *   conn.send({ message_text: "hola" });
 *   conn.close();
 */
import { BACKEND_URL } from "./api";

function toWsUrl(base) {
  if (base.startsWith("https://")) return "wss://" + base.slice(8);
  if (base.startsWith("http://")) return "ws://" + base.slice(7);
  return base;
}

export function openChatSocket({
  kind,                // "customer" | "user"
  organizationId,
  toCustomerAccountId, // opcional: para que el cliente recuerde el destino al enviar
  onMessage,
  onOpen,
  onClose,
  onError,
}) {
  let ws = null;
  let closedByUser = false;
  let reconnectAttempts = 0;
  let heartbeat = null;
  let reconnectTimer = null;

  const wsBase = toWsUrl(BACKEND_URL);
  // Sprint H Batch 2 · Auth EXCLUSIVAMENTE por cookie httpOnly (mismo origen
  // → browser envía cookie automáticamente en el handshake WS).
  const qs = new URLSearchParams({ kind, organization_id: organizationId });
  const url = `${wsBase}/api/chats/ws?${qs.toString()}`;

  const connect = () => {
    if (closedByUser) return;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      scheduleReconnect();
      return;
    }
    ws.onopen = () => {
      reconnectAttempts = 0;
      heartbeat = setInterval(() => {
        if (ws && ws.readyState === WebSocket.OPEN) {
          try { ws.send(JSON.stringify({ type: "ping" })); } catch (e) {}
        }
      }, 25000);
      onOpen?.();
    };
    ws.onmessage = (e) => {
      let data = null;
      try { data = JSON.parse(e.data); } catch (err) { return; }
      onMessage?.(data);
    };
    ws.onerror = (e) => { onError?.(e); };
    ws.onclose = () => {
      if (heartbeat) { clearInterval(heartbeat); heartbeat = null; }
      onClose?.();
      if (!closedByUser) scheduleReconnect();
    };
  };

  const scheduleReconnect = () => {
    reconnectAttempts += 1;
    const delay = Math.min(30000, 1000 * Math.pow(2, Math.min(reconnectAttempts, 5)));
    reconnectTimer = setTimeout(connect, delay);
  };

  connect();

  return {
    send: (obj) => {
      if (!ws || ws.readyState !== WebSocket.OPEN) return false;
      const payload = { ...obj };
      if (kind === "user" && toCustomerAccountId && !payload.to_customer_account_id) {
        payload.to_customer_account_id = toCustomerAccountId;
      }
      try { ws.send(JSON.stringify(payload)); return true; } catch (e) { return false; }
    },
    isOpen: () => ws && ws.readyState === WebSocket.OPEN,
    close: () => {
      closedByUser = true;
      if (heartbeat) { clearInterval(heartbeat); heartbeat = null; }
      if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
      if (ws) { try { ws.close(); } catch (e) {} }
    },
  };
}
